import { useCallback, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWebsocket } from "@finitless/shared/contexts";

/**
 * Hook for handling real-time conversation messages via websockets
 * Specifically designed for conversation detail pages
 */
export const useConversationMessagesSocket = (conversationId) => {
  const queryClient = useQueryClient();
  const { isConnected, registerEventListener, unregisterEventListener } =
    useWebsocket();
  const [listenersSetup, setListenersSetup] = useState(false);
  const hookId = useRef(
    `useConversationMessagesSocket-${Date.now()}-${Math.random()}`,
  );

  /**
   * Handle new conversation messages
   */
  const handleNewConversationMessage = useCallback(
    (messageData) => {
      queryClient.setQueriesData(
        { queryKey: ["conversationMessages", conversationId] },
        (oldData) => {
          if (!oldData?.messages) {
            return {
              messages: [messageData],
              pagination: { total: 1, page: 1, page_size: 50 },
            };
          }

          const messageExists = oldData.messages.some(
            (msg) => msg.id === messageData.id,
          );
          if (messageExists) {
            return oldData;
          }

          const firstMessageDate =
            oldData.messages.length > 0
              ? new Date(oldData.messages[0].created_at)
              : null;
          const lastMessageDate =
            oldData.messages.length > 0
              ? new Date(
                  oldData.messages[oldData.messages.length - 1].created_at,
                )
              : null;

          let updatedMessages;
          if (!firstMessageDate || !lastMessageDate) {
            updatedMessages = [messageData];
          } else {
            const isDescending = firstMessageDate > lastMessageDate;

            if (isDescending) {
              updatedMessages = [messageData, ...oldData.messages];
            } else {
              updatedMessages = [...oldData.messages, messageData];
            }
          }

          return {
            ...oldData,
            messages: updatedMessages,
            pagination: {
              ...oldData.pagination,
              total: (oldData.pagination.total || 0) + 1,
            },
          };
        },
      );

      // Also update the conversation's last_message_at and messages_count
      queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
        if (!oldData?.conversations) return oldData;

        const updatedConversations = oldData.conversations.map(
          (conversation) => {
            if (conversation.id === conversationId) {
              return {
                ...conversation,
                last_message_at: messageData.created_at,
                messages_count: (conversation.messages_count || 0) + 1,
              };
            }
            return conversation;
          },
        );

        return {
          ...oldData,
          conversations: updatedConversations,
        };
      });

      // Update individual conversation query
      queryClient.setQueryData(
        ["conversation", conversationId],
        (oldConversation) => {
          if (!oldConversation) return oldConversation;

          return {
            ...oldConversation,
            last_message_at: messageData.created_at,
            messages_count: (oldConversation.messages_count || 0) + 1,
          };
        },
      );
    },
    [queryClient, conversationId],
  );

  /**
   * Handle conversation updates
   */
  const handleConversationUpdate = useCallback(
    (conversationData) => {
      // Only process updates for the current conversation
      if (conversationData.id !== conversationId) {
        return;
      }

      // Update individual conversation query
      queryClient.setQueryData(
        ["conversation", conversationId],
        (oldConversation) => {
          if (!oldConversation) return oldConversation;

          return {
            ...oldConversation,
            ...conversationData,
          };
        },
      );

      // Update conversations list
      queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
        if (!oldData?.conversations) return oldData;

        const updatedConversations = oldData.conversations.map(
          (conversation) =>
            conversation.id === conversationId
              ? { ...conversation, ...conversationData }
              : conversation,
        );

        return {
          ...oldData,
          conversations: updatedConversations,
        };
      });
    },
    [queryClient, conversationId],
  );

  // Use refs to maintain stable references
  const handleNewConversationMessageRef = useRef(handleNewConversationMessage);
  const handleConversationUpdateRef = useRef(handleConversationUpdate);

  // Update refs when handlers change
  useEffect(() => {
    handleNewConversationMessageRef.current = handleNewConversationMessage;
    handleConversationUpdateRef.current = handleConversationUpdate;
  }, [handleNewConversationMessage, handleConversationUpdate]);

  /**
   * Setup socket listeners using centralized event system
   */
  const setupSocketListeners = useCallback(() => {
    if (!isConnected || listenersSetup || !conversationId) {
      return;
    }

    // Register with centralized event system
    registerEventListener(
      "conversation.message.created",
      handleNewConversationMessage,
      hookId.current,
    );
    registerEventListener(
      "conversation.updated",
      handleConversationUpdate,
      hookId.current,
    );

    setListenersSetup(true);
  }, [
    isConnected,
    conversationId,
    handleNewConversationMessage,
    handleConversationUpdate,
    listenersSetup,
    registerEventListener,
  ]);

  /**
   * Cleanup socket listeners using centralized event system
   */
  const cleanupSocketListeners = useCallback(() => {
    if (!listenersSetup) return;

    // Unregister from centralized event system
    unregisterEventListener("conversation.message.created", hookId.current);
    unregisterEventListener("conversation.updated", hookId.current);

    setListenersSetup(false);
  }, [listenersSetup, unregisterEventListener, conversationId]);

  // Setup listeners when connected and conversationId is available
  useEffect(() => {
    if (isConnected && conversationId && !listenersSetup) {
      setupSocketListeners();
    }
  }, [isConnected, conversationId, setupSocketListeners]);

  // Cleanup listeners on unmount or when conversationId changes
  useEffect(() => {
    return () => {
      cleanupSocketListeners();
    };
  }, [cleanupSocketListeners]);

  return {
    isConnected,
    listenersSetup,
    conversationId,
  };
};

export default useConversationMessagesSocket;
